import { useState } from 'react';
import PropTypes from 'prop-types';
import ErrorMessage from '../common/ErrorMessage';

const formatDate = (value) => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }
    return date.toLocaleString();
};

export function IssueReportsList({
    reports,
    loading,
    error,
    onRetry,
    reasonLookup,
    deviceLookup,
    problemAreaLookup,
    problemServiceLookup,
    problemAreasLoading,
    problemAreasError,
    onRetryProblemAreas,
    problemServicesLoading,
    problemServicesError,
    onRetryProblemServices,
    onCloseReport,
    emptyMessage = 'no reported issues',
}) {
    const [closingIds, setClosingIds] = useState(() => new Set());

    if (loading) {
        return <div className="issue-reports-empty">Loading reported issues…</div>;
    }

    if (error) {
        return (
            <div className="issue-reports-error">
                <ErrorMessage title="Unable to load reported issues" message={error} />
                {typeof onRetry === 'function' ? (
                    <div>
                        <button type="button" className="secondary-btn" onClick={onRetry}>
                            Try again
                        </button>
                    </div>
                ) : null}
            </div>
        );
    }

    if (!reports?.length) {
        return <div className="issue-reports-empty">{emptyMessage}</div>;
    }

    return (
        <div className="issue-reports-list">
            {reports.map((report) => {
                const reasonLabel = reasonLookup.get(report.report_reason) || `Reason #${report.report_reason}`;
                const device = deviceLookup.get(report.device_id);
                const deviceLabel = device
                    ? device.device_name
                        ? `${device.device_name} (${device.mac || `ID ${device.id}`})`
                        : device.mac || `Device ${device.id}`
                    : `Device ${report.device_id}`;

                const hasProblemArea = report.problem_area_id !== null && report.problem_area_id !== undefined;
                const hasProblemService = report.problem_service_id !== null && report.problem_service_id !== undefined;

                const problemAreaLabel = hasProblemArea
                    ? problemAreaLookup.get(report.problem_area_id) || `Area #${report.problem_area_id}`
                    : null;
                const problemServiceLabel = hasProblemService
                    ? problemServiceLookup.get(report.problem_service_id) || `Service #${report.problem_service_id}`
                    : null;

                return (
                    <article key={report.id} className="issue-report-card">
                        <header className="issue-report-card__header">
                            <span className="issue-report-card__reason">{reasonLabel}</span>
                            <span className="issue-report-card__date">{formatDate(report.created_at)}</span>
                        </header>
                        {typeof onCloseReport === 'function' ? (
                            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                                <button
                                    type="button"
                                    className="secondary-btn"
                                    disabled={closingIds.has(report.id)}
                                    onClick={() => {
                                        setClosingIds((prev) => {
                                            const next = new Set(prev);
                                            next.add(report.id);
                                            return next;
                                        });
                                        Promise.resolve(onCloseReport(report.id))
                                            .catch(() => { })
                                            .finally(() => {
                                                setClosingIds((prev) => {
                                                    const next = new Set(prev);
                                                    next.delete(report.id);
                                                    return next;
                                                });
                                            });
                                    }}
                                >
                                    {closingIds.has(report.id) ? 'Closing…' : 'Close issue'}
                                </button>
                            </div>
                        ) : null}
                        <dl className="issue-report-card__meta">
                            <div className="issue-report-card__meta-item">
                                <dt>Device</dt>
                                <dd>{deviceLabel}</dd>
                            </div>
                            {problemAreaLabel ? (
                                <div className="issue-report-card__meta-item">
                                    <dt>Problem area</dt>
                                    <dd>
                                        {problemAreaLabel}
                                        {problemAreasLoading ? ' (loading…)': null}
                                    </dd>
                                    {problemAreasError && typeof onRetryProblemAreas === 'function' ? (
                                        <dd>
                                            <button type="button" className="secondary-btn" onClick={onRetryProblemAreas}>
                                                Reload problem areas
                                            </button>
                                        </dd>
                                    ) : null}
                                </div>
                            ) : null}
                            {problemServiceLabel ? (
                                <div className="issue-report-card__meta-item">
                                    <dt>Service</dt>
                                    <dd>
                                        {problemServiceLabel}
                                        {problemServicesLoading ? ' (loading…)': null}
                                    </dd>
                                    {problemServicesError && typeof onRetryProblemServices === 'function' ? (
                                        <dd>
                                            <button type="button" className="secondary-btn" onClick={onRetryProblemServices}>
                                                Reload services
                                            </button>
                                        </dd>
                                    ) : null}
                                </div>
                            ) : null}
                            {report.started_at ? (
                                <div className="issue-report-card__meta-item">
                                    <dt>Started</dt>
                                    <dd>{formatDate(report.started_at)}</dd>
                                </div>
                            ) : null}
                            {report.ended_at ? (
                                <div className="issue-report-card__meta-item">
                                    <dt>Ended</dt>
                                    <dd>{formatDate(report.ended_at)}</dd>
                                </div>
                            ) : null}
                        </dl>
                        <p className="issue-report-card__description">{report.description}</p>
                    </article>
                );
            })}
        </div>
    );
}

IssueReportsList.propTypes = {
    reports: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            report_reason: PropTypes.number.isRequired,
            device_id: PropTypes.number.isRequired,
            description: PropTypes.string.isRequired,
            created_at: PropTypes.string,
            started_at: PropTypes.string,
            ended_at: PropTypes.string,
            problem_area_id: PropTypes.number,
            problem_service_id: PropTypes.number,
        })
    ),
    loading: PropTypes.bool,
    error: PropTypes.string,
    onRetry: PropTypes.func,
    reasonLookup: PropTypes.instanceOf(Map).isRequired,
    deviceLookup: PropTypes.instanceOf(Map).isRequired,
    problemAreaLookup: PropTypes.instanceOf(Map),
    problemServiceLookup: PropTypes.instanceOf(Map),
    problemAreasLoading: PropTypes.bool,
    problemAreasError: PropTypes.string,
    onRetryProblemAreas: PropTypes.func,
    problemServicesLoading: PropTypes.bool,
    problemServicesError: PropTypes.string,
    onRetryProblemServices: PropTypes.func,
    onCloseReport: PropTypes.func,
    emptyMessage: PropTypes.string,
};

IssueReportsList.defaultProps = {
    reports: [],
    loading: false,
    error: null,
    onRetry: undefined,
    problemAreaLookup: new Map(),
    problemServiceLookup: new Map(),
    problemAreasLoading: false,
    problemAreasError: null,
    onRetryProblemAreas: undefined,
    problemServicesLoading: false,
    problemServicesError: null,
    onRetryProblemServices: undefined,
    onCloseReport: undefined,
    emptyMessage: 'no reported issues',
};
